import React, {
  useImperativeHandle,
  forwardRef,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, difference, isEqual } from 'lodash';
import { Form, IWarning as IWarningCommon } from '@praktio/common-ui';
import classNames from 'classnames';

import CommonUser from '../../common/User';
import FormHeader from '../../common/FormHeader/FormHeader';
import { RootState } from '../../../ducks/store';
import {
  CompleteRegistrationFormWarning,
  GenericFormWarning,
  ResetPasswordFormWarning,
  UnmetReqsFormWarning,
  FormValidationErrors,
  PlatformMessageTypes,
  ErrorCodes,
} from '../../../utils/constants';
import { findFormErrors, handleUserFormEdit } from '../../../utils/functions';
import { useGetTeamCourses } from '../../../hooks/useGetTeamCourses';

const ComponentId = 'SETTINGS';

interface IWarning extends IWarningCommon {
  style?: 'danger' | 'info';
}

interface ISettingsContentProps {
  notifyHasChanges: (changes: boolean) => void;
  handleNetworkError: (error: IError) => void;
}

const SettingsContent = forwardRef<IRemoteUserSettingsFunctionsProps, ISettingsContentProps>(
  ({ notifyHasChanges, handleNetworkError }, ref) => {
    useImperativeHandle(ref, () => ({
      validateData,
      handleError,
      clearErrors,
    }));

    const dispatch = useDispatch();
    const { data, messages } = useSelector((state: RootState) => {
      const data = state.auth.authenticatedUser;
      return {
        data,
        messages: state.platformData.messages,
      };
    });

    const prevData = useRef<IUser | undefined>();
    const [user, setUser] = useState<IUser | undefined>(data);
    const [hasChanges, setHasChanges] = useState(!isEqual(user, prevData.current));
    const [warnings, setWarnings] = useState<IWarning[]>([]);
    const [userFormErrors, setUserFormErrors] = useState<IBootstrapFormErrors>();

    const { getTeamsCourseIds } = useGetTeamCourses();

    const handleHasChangesUpdate = useCallback(
      (newValue: boolean) => {
        setHasChanges(newValue);
        if (notifyHasChanges) {
          notifyHasChanges(newValue);
        }
      },
      [notifyHasChanges],
    );

    useEffect(() => {
      if (data && !isEqual(prevData.current, data)) {
        setUser(data);
        handleHasChangesUpdate(false);
        if (data?.unmetRequirements && !data?.forceRegistration) {
          if (!data?.forcePasswordReset) {
            setWarnings([
              ...warnings,
              { ...UnmetReqsFormWarning, style: data?.isFirstLogin ? 'info' : 'danger' },
            ]);

            if (!data.optOutCLEs && !data.cles?.length) {
              setUserFormErrors({
                cles: FormValidationErrors.cles,
              });
            }
          }
        }
      }
      prevData.current = cloneDeep(data);
    }, [data, warnings, handleHasChangesUpdate, dispatch]);

    const updateUserReportDeselectedCourses = (value: string[], user: IUser): void => {
      const prevReportTeams = prevData.current?.reportTeams || [];
      const addedTeams = difference(value as string[], prevReportTeams);
      const newTeamCoursesIds = getTeamsCourseIds(addedTeams);

      if (!newTeamCoursesIds.length) {
        return;
      }

      const newDeselectedCourses = difference(user.reportDeselectedCourses, newTeamCoursesIds);

      user.reportDeselectedCourses = newDeselectedCourses;
    };

    const prepAndExecuteUserFormEdit = (
      field: string,
      value: string | boolean | ICLEEntry | string[],
    ) => {
      let clonedUser = cloneDeep(user);
      let clonedFormErrors = cloneDeep(userFormErrors);
      let clonedWarnings = cloneDeep(warnings);

      handleUserFormEdit({ field, value, clonedUser, clonedFormErrors, clonedWarnings });

      // If field is "reportTeams", update reportDeselectedCourses to remove all courses from the newly selected teams.
      if (field === 'reportTeams') {
        updateUserReportDeselectedCourses(value as string[], clonedUser as IUser);
      }

      const newHasChanges = !isEqual(clonedUser, prevData.current);

      // Queue/Execute setStates.
      handleHasChangesUpdate(newHasChanges);
      setUser(clonedUser);
      setUserFormErrors(clonedFormErrors);
      setWarnings(clonedWarnings);
    };

    const returnValidatedUserFormData = async () => {
      if (!user) {
        return;
      }

      if (hasChanges) {
        const newErrors = await findFormErrors({
          user: user,
          cleRequired: true,
          requirePassword: user.forcePasswordReset,
          ignoreCurrentPassword: !!user.isSSOSession,
        });
        if (Object.keys(newErrors).length > 0) {
          if (!warnings.some((warning) => warning.title === 'Form Error')) {
            setWarnings([...warnings, GenericFormWarning]);
          }
          setUserFormErrors(newErrors);
          return;
        }
      }

      return {
        hasChanges: hasChanges,
        formData: user,
      };
    };

    const handleError = (error: IError) => {
      const { errorCode } = error;

      const alert =
        messages.find(
          (m) =>
            m.type === PlatformMessageTypes.Alert &&
            m.componentId?.includes(ComponentId) &&
            m.errorCode === errorCode,
        ) || GenericFormWarning;

      const formMessage = messages.find(
        (m) =>
          m.type === PlatformMessageTypes.Form &&
          m.componentId?.includes(ComponentId) &&
          m.errorCode === errorCode &&
          m.field,
      );

      switch (errorCode) {
        case ErrorCodes.InvalidPassword:
        case ErrorCodes.SamePassword:
        case ErrorCodes.InvalidEmail:
        case ErrorCodes.DuplicateEmail:
          if (formMessage) {
            setUserFormErrors({
              ...userFormErrors,
              [formMessage.field as string]: formMessage.body,
            });
          }

          setWarnings([alert]);
          break;
        default:
          handleNetworkError(error);
          break;
      }
    };

    const clearErrors = () => {
      setUserFormErrors({});
      setWarnings([]);
    };

    const validateData = async (isExternal?: boolean) => {
      return await returnValidatedUserFormData();
    };

    if (!user) {
      return null;
    }

    return (
      <div className="SettingsContent">
        {!!data?.forcePasswordReset && (
          <div className="alert alert-info" role="alert">
            <div className="alert-title">{ResetPasswordFormWarning.title}</div>
            <div className="alert-body">{ResetPasswordFormWarning.body}</div>
          </div>
        )}
        {!!data?.forceRegistration && (
          <div className="alert alert-info" role="alert">
            <div className="alert-title">{CompleteRegistrationFormWarning.title}</div>
            <div className="alert-body">{CompleteRegistrationFormWarning.body}</div>
          </div>
        )}
        {!!warnings &&
          warnings.length > 0 &&
          warnings.map((warning, i) => (
            <div
              key={`warning-${i}`}
              className={classNames('alert', `alert-${warning.style || 'danger'}`)}
              role="alert"
            >
              <div className="alert-title">{warning.title}</div>
              <div className="alert-body">{warning.body}</div>
            </div>
          ))}
        <Form
          formId="user-settings-form"
          onSubmit={(event: any) => {
            event.preventDefault();
          }}
        >
          <FormHeader formName="Profile Settings" />
          <CommonUser
            handleFormEdit={prepAndExecuteUserFormEdit}
            user={user}
            userFormErrors={userFormErrors}
            cleRequired
          />
          <div className="text-left">
            <span className="required-notice">* required</span>
          </div>
        </Form>
      </div>
    );
  },
);

export default SettingsContent;
